import { Box, Table, Tag, Text } from "@chakra-ui/react";
import { useSelector } from "react-redux";

import { ARRIVAL_RATES_FREQUENCIES, LEVELS } from "../../../store/constants";
import AgentTicketStatusGraphs from "../charts/AgentTicketStatusGraphs";
import { formatNumber } from "../../../store/helpers";
import {
  AGENT_TICKETS_PER_SERVICE_COLUMN_DEFINITIONS,
  normalizeRenderConfig,
  sortReportRows,
} from "../../../store/renderConfig";
import * as defaultSelectors from "../../../store/selectors/defaultSelector";
import ConfiguredReportTable from "../../reports/ConfiguredReportTable";

const getTicketsPerAgentRows = (branch) => {
  const totalBranchTickets = (branch.agentsData || []).reduce(
    (sum, agent) => sum + (Number(agent.total) || 0),
    0
  );
  return (branch.agentsData || [])
    .filter((agent) => Number(agent?.total))
    .map((agent) => ({
      ...agent,
      branchName: branch.branchName || "",
      totalPct: totalBranchTickets ? ((Number(agent.total) || 0) / totalBranchTickets) * 100 : 0,
    }));
};

const MetricCell = ({ value, pct, color = "inherit" }) => (
  <Text color={color}>
    {formatNumber(value)}
    {pct === undefined ? null : <Box className="agentTicketMutedCell">({Number(pct || 0).toFixed(2)}%)</Box>}
  </Text>
);

const renderCell = (agentData, columnId) => {
  if (columnId === "agentName") return agentData.agentName;
  if (columnId === "total") return <Text color="var(--green)">{formatNumber(agentData.total)}</Text>;
  if (columnId === "percentage") return `${Number(agentData.totalPct || 0).toFixed(2)}%`;
  if (columnId === "average") return formatNumber(agentData.average);
  if (columnId === "min") return formatNumber(agentData.min);
  if (columnId === "max") return formatNumber(agentData.max);
  if (columnId === "completed") return <MetricCell value={agentData.numCompleted} pct={agentData.pctCompleted} color="var(--green)" />;
  if (columnId === "noShow") return <MetricCell value={agentData.numNoShow} pct={agentData.pctNoShow} color="var(--red)" />;
  if (columnId === "unattended") return <MetricCell value={agentData.numUnattended} pct={agentData.pctUnattended} color="var(--red)" />;
  return null;
};

const TicketsPerAgentTable = ({ data, reportIndex, branchIndex }) => {
  const renderConfig = useSelector(defaultSelectors.selectRenderConfig);
  const config = normalizeRenderConfig(renderConfig, {
    columnDefinitions: AGENT_TICKETS_PER_SERVICE_COLUMN_DEFINITIONS,
  });
  const { agentsData = [], branchName } = data;
  const rows = sortReportRows(getTicketsPerAgentRows(data), config, {
    columnDefinitions: AGENT_TICKETS_PER_SERVICE_COLUMN_DEFINITIONS,
  });

  return (
    <Box>
      <Text>{agentsData.length} agents.</Text>
      {!agentsData.length ? <Text fontSize="sm">No data available for this report</Text> : null}

      {agentsData.length && !config.charts.enabled ? (
        <Box className="reportTableContainer ticketsPerAgentBlock">
          <ConfiguredReportTable
            columns={AGENT_TICKETS_PER_SERVICE_COLUMN_DEFINITIONS}
            rows={rows}
            renderConfig={config}
            renderCell={renderCell}
            presortedRows={true}
          />
        </Box>
      ) : null}

      {agentsData.length && config.charts.enabled
        ? rows.map((agentData, index) => {
            const title = `${reportIndex || ""}.${branchIndex + 1}.${index + 1}. ${
              agentData.agentName
            }`;
            return (
              <Box key={`${reportIndex}-${branchIndex}-${index}`} className="reportContainer" mt="20px">
                <h3>{title}</h3>
                <Tag.Root>
                  <Tag.Label className="branchTag">{branchName} Branch</Tag.Label>
                </Tag.Root>
                <Table.Root className="stripedTable" size="sm">
                  <Table.Header>
                    <Table.Row>
                      <Table.ColumnHeader>#</Table.ColumnHeader>
                      <Table.ColumnHeader>Total Tickets</Table.ColumnHeader>
                      <Table.ColumnHeader>Average</Table.ColumnHeader>
                      <Table.ColumnHeader>Min</Table.ColumnHeader>
                      <Table.ColumnHeader>Max</Table.ColumnHeader>
                      <Table.ColumnHeader>Completed</Table.ColumnHeader>
                      <Table.ColumnHeader>No Show</Table.ColumnHeader>
                      <Table.ColumnHeader>Unattended</Table.ColumnHeader>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    <Table.Row>
                      <Table.Cell>{index + 1}</Table.Cell>
                      <Table.Cell>{renderCell(agentData, "total")}</Table.Cell>
                      <Table.Cell>{renderCell(agentData, "average")}</Table.Cell>
                      <Table.Cell>{renderCell(agentData, "min")}</Table.Cell>
                      <Table.Cell>{renderCell(agentData, "max")}</Table.Cell>
                      <Table.Cell>{renderCell(agentData, "completed")}</Table.Cell>
                      <Table.Cell>{renderCell(agentData, "noShow")}</Table.Cell>
                      <Table.Cell>{renderCell(agentData, "unattended")}</Table.Cell>
                    </Table.Row>
                    <Table.Row>
                      <Table.Cell colSpan={8}>
                        <Box mt="20px" mb="20px">
                          <AgentTicketStatusGraphs
                            charts={agentData.charts}
                            frequency={ARRIVAL_RATES_FREQUENCIES.DAY}
                            level={LEVELS.BRANCH}
                            title={`${agentData.agentName} Tickets, Day by Day Comparison`}
                          />
                        </Box>
                      </Table.Cell>
                    </Table.Row>
                  </Table.Body>
                </Table.Root>
              </Box>
            );
          })
        : null}
    </Box>
  );
};

export default TicketsPerAgentTable;
